import React from 'react';
import styles from './CustomMenuItem.module.css';

/**
 * CustomMenuItem - Replacement for Bootstrap's MenuItem to prevent nested anchor tags
 *
 * Problem: Bootstrap's MenuItem component renders an <a> tag internally. When wrapped
 * with our custom Link component (which also renders <a>), this creates invalid nested
 * <a><a></a></a> HTML, breaking navigation and accessibility.
 *
 * Context: During migration from Next.js Link to React Router Link, the Link component
 * was simplified to always render <a> tags. Previously, Next.js Link had special handling
 * for Bootstrap components, but this complexity was removed in the migration.
 *
 * Solution: This component renders a <div> instead of <a> inside the <li>, avoiding
 * nested anchors while maintaining Bootstrap dropdown styling and functionality.
 *
 * Usage: Replace MenuItem with CustomMenuItem when wrapping with Link components:
 *   <SomeLink><CustomMenuItem>Content</CustomMenuItem></SomeLink>
 */
const CustomMenuItem = ({
  children,
  onClick,
  divider,
  header,
  disabled,
  className,
  ...props
}) => {
  if (divider) {
    return <div role="separator" className="divider" />;
  }

  const handleClick = (e) => {
    if (disabled) {
      e.preventDefault();
      return;
    }
    if (onClick) {
      onClick(e);
    }
  };

  const menuItemClass =
    `${styles.menuItem} ${disabled ? styles.disabled : ''} ${className || ''}`.trim();

  return (
    <div
      role="presentation"
      style={{ cursor: 'pointer' }}
      className={disabled ? 'disabled' : ''}
    >
      <div
        role="menuitem"
        tabIndex="-1"
        onClick={handleClick}
        className={menuItemClass}
        {...props}
      >
        {children}
      </div>
    </div>
  );
};

export default CustomMenuItem;
